import { Post } from '../types/post';
import { VideoPost } from '../types/video';

/**
 * 動画の長さをフォーマット（秒数を "MM:SS" 形式に変換）
 */
export const formatDuration = (seconds: number | undefined | null): string | null => {
  if (typeof seconds !== 'number' || seconds <= 0) return null;
  
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 再生回数をフォーマット
 */
export const formatViews = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

/**
 * 相対時間をフォーマット（例: "2日前"）
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  
  // 未来の日付の場合
  if (diffInMs < 0) {
    return 'たった今';
  }
  
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears}年前`;
  }
  if (diffInMonths > 0) {
    return `${diffInMonths}ヶ月前`;
  }
  if (diffInWeeks > 0) {
    return `${diffInWeeks}週間前`;
  }
  if (diffInDays > 0) {
    return `${diffInDays}日前`;
  }
  if (diffInHours > 0) {
    return `${diffInHours}時間前`;
  }
  if (diffInMinutes > 0) {
    return `${diffInMinutes}分前`;
  }
  if (diffInSeconds > 0) {
    return `${diffInSeconds}秒前`;
  }
  return 'たった今';
};

/**
 * 動画の再生時間を取得（秒単位）
 */
export const getVideoDuration = (post: Post): number | null => {
  const findLength = (obj: any): number | null => {
    if (!obj || typeof obj !== 'object') return null;
    
    // 直接のプロパティをチェック
    const candidates = [
      obj.duration,
      obj.length,
      obj.video?.duration,
      obj.video?.length
    ];

    for (const val of candidates) {
      if (typeof val === 'number' && val > 0) {
        // Blueskyのネイティブ動画は現在最大60秒なので、500（500秒=8.3分）を超える場合はミリ秒とみなす
        // 外部動画（YouTube等）の場合も考慮し、常識的な範囲で判定
        return val > 10000 ? Math.floor(val / 1000) : Math.floor(val);
      }
    }

    // metadataオブジェクト内をチェック
    const meta = obj.metadata || obj.video?.metadata;
    if (meta) {
      if (typeof meta.duration === 'number' && meta.duration > 0) {
        return meta.duration > 36000 ? Math.floor(meta.duration / 1000) : Math.floor(meta.duration);
      }
      if (typeof meta.length === 'number' && meta.length > 0) {
        return meta.length > 36000 ? Math.floor(meta.length / 1000) : Math.floor(meta.length);
      }
    }

    return null;
  };

  let length: number | null = null;
  
  // 1. レコードの埋め込み (最も信頼できるメタデータソース)
  if (post.record?.embed) {
    length = findLength(post.record.embed) || findLength(post.record.embed.video);
    
    // recordWithMedia の場合
    if (!length && post.record.embed.media) {
      length = findLength(post.record.embed.media);
    }
  }
  
  // 2. 投稿の埋め込み (Viewデータ)
  if (!length && post.embed) {
    length = findLength(post.embed);
    if (!length && (post.embed as any).media) {
      length = findLength((post.embed as any).media);
    }
    if (!length && (post.embed as any).record) {
      const record = (post.embed as any).record;
      length = findLength(record.embed) || findLength(record.value?.embed) || findLength(record);
    }
  }

  return length;
};

/**
 * PostをVideoPostに変換
 */
export const convertPostToVideoPost = (post: Post): VideoPost | null => {
  if (!post.embed) return null;

  let videoData: { thumbnailUrl: string; videoUrl: string; duration?: string; type: 'native' | 'youtube' } | null = null;

  // 動画データの抽出とともに、メタデータが含まれる可能性のあるオブジェクトも特定する
  let resolvedEmbed = post.embed as any;

  // 動画データの抽出を試みる関数
  const extractVideo = (embed: any): typeof videoData => {
    if (!embed) return null;

    // ネイティブ動画の場合
    if (embed.$type === 'app.bsky.embed.video#view' || embed.$type === 'app.bsky.embed.video') {
      resolvedEmbed = embed;
      return {
        thumbnailUrl: embed.thumbnail || (embed.video?.thumbnail) || '',
        videoUrl: embed.playlist || (embed.video?.playlist) || '',
        type: 'native'
      };
    }

    // メディア付きレコード（画像+引用など）の場合
    if (embed.$type === 'app.bsky.embed.recordWithMedia#view' || embed.$type === 'app.bsky.embed.recordWithMedia') {
      const mediaResult = extractVideo(embed.media);
      if (mediaResult) return mediaResult;
      
      return extractVideo(embed.record?.embed) || extractVideo(embed.record?.record?.embed);
    }

    // 引用投稿のみの場合
    if (embed.$type === 'app.bsky.embed.record#view' || embed.$type === 'app.bsky.embed.record') {
      // 引用されたレコード内の埋め込みをチェック
      const record = embed.record;
      if (!record) return null;

      // embeds配列がある場合
      if (Array.isArray(record.embeds)) {
        for (const e of record.embeds) {
          const found = extractVideo(e);
          if (found) return found;
        }
      }
      
      // 単一のembedの場合 (record.embed or record.value.embed)
      const targetEmbed = record.embed || record.value?.embed || (record.record?.embed);
      if (targetEmbed) {
        return extractVideo(targetEmbed);
      }
    }

    // YouTubeリンク（外部リンク）の場合
    if (
      embed.$type === 'app.bsky.embed.external#view' &&
      embed.external?.uri?.match(/youtube\.com|youtu\.be/)
    ) {
      resolvedEmbed = embed;
      return {
        thumbnailUrl: embed.external.thumb || '',
        videoUrl: embed.external.uri,
        type: 'youtube'
      };
    }

    return null;
  };

  videoData = extractVideo(post.embed);

  if (!videoData) return null;
  
  // 動画の元投稿者情報（常にpost.authorを使用）
  let author = post.author;
  let createdAt = post.record.createdAt;
  let description = post.record.text;
  let embedData = resolvedEmbed; // 特定された埋め込みデータを使用
  let originalUri = post.uri;
  let originalCid = post.cid;
  let likeCount = post.likeCount;
  let likeUri = post.viewer?.like;
  
  // 引用投稿（recordWithMedia）の場合、メディアは引用元から取得するが、投稿者は現在の投稿者を使用
  if (post.embed.$type === 'app.bsky.embed.recordWithMedia#view') {
    embedData = (post.embed as any).media;
    // 引用されたレコードの情報は使用しない（投稿者は元のまま）
    const quotedRecord = (post.embed as any).record?.record;
    if (quotedRecord) {
      // 引用元のURIとCIDのみ保存（いいね等の操作用）
      originalUri = quotedRecord.uri || originalUri;
      originalCid = quotedRecord.cid || originalCid;
    }
  } else if (post.embed.$type === 'app.bsky.embed.record#view' && (post.embed as any).record?.author) {
    // 純粋な引用投稿の場合も、投稿者は現在の投稿者を維持
    const quotedRecord = (post.embed as any).record;
    if (quotedRecord) {
      originalUri = quotedRecord.uri || originalUri;
      originalCid = quotedRecord.cid || originalCid;
    }
  }
  
  // 動画の長さを取得（秒単位）
  const length = getVideoDuration(post);
  const duration = formatDuration(length) || '';
  
  // タイトルを生成（外部動画のタイトル、または投稿テキストの最初の100文字）
  const title = embedData?.external?.title || (description.length > 100 
    ? description.substring(0, 97) + '...'
    : description || 'Untitled Video');

  return {
    id: post.cid,
    uri: post.uri,
    cid: post.cid,
    originalUri,
    originalCid,
    thumbnailUrl: videoData.thumbnailUrl,
    videoUrl: videoData.videoUrl,
    type: videoData.type,
    title,
    description: description,
    channelName: author.displayName || author.handle,
    channelHandle: author.handle,
    channelAvatar: author.avatar,
    authorDid: author.did,
    likeCount: likeCount,
    likeUri: likeUri,
    repostCount: post.repostCount || 0,
    repostUri: post.viewer?.repost,
    commentCount: post.replyCount || 0,
    createdAt: formatRelativeTime(createdAt),
    duration: duration,
    aspectRatio: embedData?.video?.aspectRatio,
  };
};
